import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export const PLANS = [
  {
    key: "pro",
    name: "Pro",
    tagline: "For the solo voyager",
    monthly: { lookup_key: "pins_pro_monthly", price: "$2.99", per: "/mo" },
    yearly: { lookup_key: "pins_pro_yearly", price: "$24.99", per: "/yr" },
    savings: "Save 30% yearly",
    features: [
      "Live location sharing",
      "AI trip recaps",
      "30 photos per pin · 25MB each",
      "Planned pins & everything free",
    ],
  },
  {
    key: "family",
    name: "Family",
    tagline: "You + up to 5 travellers",
    monthly: { lookup_key: "pins_family_monthly", price: "$7.99", per: "/mo" },
    yearly: { lookup_key: "pins_family_yearly", price: "$59.99", per: "/yr" },
    savings: "Save 37% yearly",
    features: [
      "Everything in Pro",
      "Share with up to 5 members",
      "Members get full Pro access",
      "One bill for the whole crew",
    ],
  },
];

export default function PlanPicker({ compact = false }) {
  const [interval, setInterval_] = useState("yearly");
  const [busy, setBusy] = useState("");

  const subscribe = async (plan) => {
    const lookup_key = plan[interval].lookup_key;
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy("");
    }
  };

  return (
    <div data-testid="plan-picker">
      <div className="flex items-center justify-center gap-0 mb-5">
        {["monthly", "yearly"].map((iv) => (
          <button
            key={iv}
            type="button"
            data-testid={`interval-${iv}`}
            onClick={() => setInterval_(iv)}
            className={`px-4 py-1.5 border font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
              interval === iv
                ? "bg-[#1f1f1f] text-[#f7f5f0] border-[#1f1f1f]"
                : "bg-transparent text-[#1f1f1f] border-[#d5cfc1] hover:border-[#1f1f1f]"
            }`}
          >
            {iv === "monthly" ? "Monthly" : "Yearly"}
          </button>
        ))}
      </div>

      <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
        {PLANS.map((plan) => (
          <div
            key={plan.key}
            data-testid={`plan-card-${plan.key}`}
            className={`tactile-card p-5 flex flex-col ${plan.key === "family" ? "bg-[#efebe1]" : ""}`}
          >
            <p className="overline">{plan.tagline}</p>
            <h3 className="font-serif text-3xl mt-1 text-[#1f1f1f]">{plan.name}</h3>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-serif text-4xl text-[#2c4a3e] tabular-nums">
                {plan[interval].price}
              </span>
              <span className="font-mono text-xs text-[#5c5c5c]">{plan[interval].per}</span>
            </div>
            {interval === "yearly" && (
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#b85c47] mt-1">
                {plan.savings}
              </p>
            )}
            <ul className="mt-4 space-y-2 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[#1f1f1f]">
                  <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#2c4a3e]" strokeWidth={2} />
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              data-testid={`subscribe-${plan.key}-button`}
              onClick={() => subscribe(plan)}
              disabled={!!busy}
              className={`mt-5 w-full flex items-center justify-center gap-2 ${
                plan.key === "family" ? "btn-terracotta" : "btn-primary"
              }`}
            >
              {busy === plan[interval].lookup_key && (
                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
              )}
              Choose {plan.name}
            </button>
          </div>
        ))}
      </div>
      <p
        data-testid="coupon-checkout-note"
        className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#2c4a3e] mt-4 text-center"
      >
        Have a coupon code? Enter it at checkout.
      </p>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c] mt-2 text-center">
        Test mode · use card 4242 4242 4242 4242 · cancel anytime
      </p>
    </div>
  );
}
